import React, { useMemo, useState } from "react";
import "./Cart.css";

const currency = (n) => `$${n.toFixed(2)}`;

const INITIAL_ITEMS = [
  {
    id: "bqt-rose-deluxe",
    name: "Rose Romance Deluxe",
    description: "Hand-tied pink roses, eucalyptus & seasonal greens",
    image:
      "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=800&q=80",
    price: 69.0,
    qty: 1,
    size: "Standard",
  },
  {
    id: "bqt-peony-bliss",
    name: "Peony Bliss",
    description: "Soft peonies & ranunculus in blush palette",
    image:
      "https://images.unsplash.com/photo-1526045478516-99145907023c?auto=format&fit=crop&w=800&q=80",
    price: 89.0,
    qty: 1,
    size: "Premium",
  },
];

export default function Cart() {
  const [items, setItems] = useState(INITIAL_ITEMS);
  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [shipping, setShipping] = useState("standard"); // standard | express

  const FREE_SHIP_THRESHOLD = 120; // show progress to free delivery

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + it.price * it.qty, 0),
    [items]
  );

  const promoDiscount = useMemo(() => {
    if (!appliedPromo) return 0;
    // Demo: TULIP10 => 10% off
    if (appliedPromo === "TULIP10") return subtotal * 0.1;
    return 0;
  }, [appliedPromo, subtotal]);

  const shippingCost = useMemo(() => {
    if (subtotal >= FREE_SHIP_THRESHOLD) return 0;
    return shipping === "express" ? 12 : 6;
  }, [shipping, subtotal]);

  const taxEstimate = useMemo(() => subtotal * 0.06, [subtotal]); // demo 6% tax
  const total = Math.max(0, subtotal - promoDiscount) + shippingCost + taxEstimate;

  const remainingForFreeShip = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);
  const freeShipProgress = Math.min(100, (subtotal / FREE_SHIP_THRESHOLD) * 100);

  // Actions
  const updateQty = (id, delta) => {
    setItems((prev) =>
      prev
        .map((it) => (it.id === id ? { ...it, qty: Math.max(1, it.qty + delta) } : it))
        .filter((it) => it.qty > 0)
    );
  };

  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const applyPromo = () => {
    const code = promo.trim().toUpperCase();
    if (!code) return;
    setAppliedPromo(code);
  };

  const clearPromo = () => {
    setAppliedPromo(null);
    setPromo("");
  };

  return (
    <main className="cart-page" aria-label="Shopping Cart">
      <div className="cart-wrap">
        {/* Left: Items */}
        <section className="cart-items" aria-label="Cart items">
          <header className="cart-header">
            <h1 className="cart-title">Your Cart</h1>
            <p className="cart-subtitle">{items.length} item{items.length !== 1 ? "s" : ""}</p>
          </header>

          {/* Free delivery progress */}
          <div className="free-ship">
            {subtotal >= FREE_SHIP_THRESHOLD ? (
              <p className="free-ship__text">
                🌸 You’ve unlocked <strong>FREE delivery</strong>!
              </p>
            ) : (
              <p className="free-ship__text">
                Add <strong>{currency(remainingForFreeShip)}</strong> more to get{" "}
                <strong>FREE delivery</strong>.
              </p>
            )}
            <div className="free-ship__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(freeShipProgress)}>
              <span className="free-ship__fill" style={{ width: `${freeShipProgress}%` }} />
            </div>
          </div>

          {items.length === 0 ? (
            <div className="cart-empty">
              <p>Your cart is feeling a little lonely.</p>
              <a className="btn-primary" href="/shop">Browse bouquets</a>
            </div>
          ) : (
            <ul className="item-list">
              {items.map((it) => (
                <li key={it.id} className="item">
                  <div className="item__imgWrap">
                    <img src={it.image} alt={it.name} className="item__img" loading="lazy" decoding="async" />
                  </div>

                  <div className="item__main">
                    <h2 className="item__name">{it.name}</h2>
                    <p className="item__desc">{it.description}</p>

                    <div className="item__meta">
                      <label className="item__opt">
                        Size
                        <select
                          className="item__select"
                          value={it.size}
                          onChange={(e) =>
                            setItems((prev) =>
                              prev.map((x) =>
                                x.id === it.id ? { ...x, size: e.target.value } : x
                              )
                            )
                          }
                        >
                          <option>Standard</option>
                          <option>Premium</option>
                          <option>Deluxe</option>
                        </select>
                      </label>

                      <div className="item__qty">
                        <button
                          className="qty-btn"
                          onClick={() => updateQty(it.id, -1)}
                          aria-label={`Decrease quantity of ${it.name}`}
                        >
                          −
                        </button>
                        <input
                          className="qty-input"
                          type="number"
                          min="1"
                          value={it.qty}
                          onChange={(e) =>
                            setItems((prev) =>
                              prev.map((x) =>
                                x.id === it.id
                                  ? { ...x, qty: Math.max(1, Number(e.target.value) || 1) }
                                  : x
                              )
                            )
                          }
                          aria-label={`Quantity for ${it.name}`}
                        />
                        <button
                          className="qty-btn"
                          onClick={() => updateQty(it.id, +1)}
                          aria-label={`Increase quantity of ${it.name}`}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="item__actions">
                      <button className="link-btn" onClick={() => removeItem(it.id)}>
                        Remove
                      </button>
                      <button className="link-btn">Save for later</button>
                    </div>
                  </div>

                  <div className="item__price">
                    <span className="item__unit">{currency(it.price)}</span>
                    <span className="item__subtotal" aria-label="Item subtotal">
                      {currency(it.price * it.qty)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Promo & Shipping tools (mobile duplicated below summary on desktop) */}
          <div className="cart-tools cart-tools--mobile">
            <PromoBox
              promo={promo}
              setPromo={setPromo}
              appliedPromo={appliedPromo}
              applyPromo={applyPromo}
              clearPromo={clearPromo}
            />
            <ShippingBox shipping={shipping} setShipping={setShipping} />
        </div>
        </section>

        {/* Right: Summary */}
        <aside className="cart-summary" aria-label="Order Summary">
          <div className="summary-card">
            <h2 className="summary-title">Order Summary</h2>

            <div className="summary-row">
              <span>Subtotal</span>
              <span>{currency(subtotal)}</span>
            </div>

            <div className="summary-row">
              <span>Discount {appliedPromo ? `(${appliedPromo})` : ""}</span>
              <span className={promoDiscount ? "good" : ""}>−{currency(promoDiscount)}</span>
            </div>

            <div className="summary-row">
              <span>Shipping</span>
              <span>{shippingCost === 0 ? "FREE" : currency(shippingCost)}</span>
            </div>

            <div className="summary-row">
              <span>Tax (est.)</span>
              <span>{currency(taxEstimate)}</span>
            </div>

            <div className="summary-total">
              <span>Total</span>
              <span>{currency(total)}</span>
            </div>

            <button className="btn-checkout" type="button">
              Proceed to Checkout
            </button>

            <div className="summary-divider" />

            {/* Tools (desktop) */}
            <div className="cart-tools cart-tools--desktop">
              <PromoBox
                promo={promo}
                setPromo={setPromo}
                appliedPromo={appliedPromo}
                applyPromo={applyPromo}
                clearPromo={clearPromo}
              />
              <ShippingBox shipping={shipping} setShipping={setShipping} />
            </div>

            <p className="summary-note">
              💌 We hand-write your personal note and include it for free.
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
}

/* ---------- Subcomponents ---------- */

function PromoBox({ promo, setPromo, appliedPromo, applyPromo, clearPromo }) {
  return (
    <div className="tool tool--promo" aria-label="Promo code">
      <label htmlFor="promo" className="tool__label">
        Promo Code
      </label>
      <div className="tool__row">
        <input
          id="promo"
          className="tool__input"
          placeholder="Enter code (try TULIP10)"
          value={promo}
          onChange={(e) => setPromo(e.target.value)}
        />
        {appliedPromo ? (
          <button className="tool__btn tool__btn--ghost" type="button" onClick={clearPromo}>
            Remove
          </button>
        ) : (
          <button className="tool__btn" type="button" onClick={applyPromo}>
            Apply
          </button>
        )}
      </div>
      <p className="tool__hint">One code per order. Discounts apply to bouquet items only.</p>
    </div>
  );
}

function ShippingBox({ shipping, setShipping }) {
  return (
    <div className="tool tool--ship" aria-label="Shipping method">
      <span className="tool__label">Delivery</span>
      <div className="ship-options">
        <label className={`ship-opt ${shipping === "standard" ? "ship-opt--active" : ""}`}>
          <input
            type="radio"
            name="ship"
            value="standard"
            checked={shipping === "standard"}
            onChange={() => setShipping("standard")}
          />
          <div className="ship-opt__content">
            <span className="ship-opt__title">Standard</span>
            <span className="ship-opt__sub">1–2 days • $6 (FREE over $120)</span>
          </div>
        </label>

        <label className={`ship-opt ${shipping === "express" ? "ship-opt--active" : ""}`}>
          <input
            type="radio"
            name="ship"
            value="express"
            checked={shipping === "express"}
            onChange={() => setShipping("express")}
          />
          <div className="ship-opt__content">
            <span className="ship-opt__title">Express</span>
            <span className="ship-opt__sub">Same-day • $12</span>
          </div>
        </label>
      </div>
    </div>
  );
}
